import React from 'react';
import { Stack, Typography } from '@mui/material';
import { intervalToDuration } from 'date-fns';

interface CounterProps {
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
    timeInSeconds?: number;
    color?: string;
    showLabels?: boolean;
    format?: 'compact' | 'detailed';
}

const Counter: React.FC<CounterProps> = ({
    days,
    hours,
    minutes,
    seconds,
    timeInSeconds,
    color = 'green',
    showLabels = false,
    format = 'compact'
}) => {
    const formatNumber = (value: number | undefined) => {
        if (value === undefined || value < 0) return '00';
        return String(Math.floor(value)).padStart(2, '0');
    };

    // Función para formatear duración de timeInSeconds
    const formatDurationFromSeconds = (totalSeconds: number) => {
        if (totalSeconds < 60) {
            return `${totalSeconds} s`;
        }

        const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 });

        if (format === 'detailed' && showLabels) {
            const parts = [];
            if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
            if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
            if (duration.minutes && duration.minutes > 0) parts.push(`${duration.minutes}m`);
            if (duration.seconds && duration.seconds > 0) parts.push(`${duration.seconds}s`);
            return parts.join(' ');
        }

        if (duration.days && duration.days > 0) {
            return `${formatNumber(duration.days)}:${formatNumber(duration.hours)}:${formatNumber(
                duration.minutes
            )}:${formatNumber(duration.seconds)}`;
        } else if (duration.hours && duration.hours > 0) {
            return `${formatNumber(duration.hours)}:${formatNumber(duration.minutes)}:${formatNumber(
                duration.seconds
            )}`;
        } else {
            return `${formatNumber(duration.minutes)}:${formatNumber(duration.seconds)}`;
        }
    };

    // Modo: timeInSeconds para duración completa
    if (timeInSeconds !== undefined) {
        return (
            <Typography component="span" fontSize={18} fontWeight={600} color={color}>
                {formatDurationFromSeconds(timeInSeconds)}
            </Typography>
        );
    }

    // Modo: contador en tiempo real con componentes separados
    const timeComponents = [];

    if (days !== undefined && days > 0) {
        timeComponents.push(
            showLabels ? (
                <Typography key="days" component="span" fontSize={18} fontWeight={600} color={color}>
                    {`${formatNumber(days)}d`}
                </Typography>
            ) : (
                <Typography key="days" component="span" fontSize={18} fontWeight={600} color={color}>
                    {`${formatNumber(days)}:`}
                </Typography>
            )
        );
    }

    if (hours !== undefined && hours > 0) {
        timeComponents.push(
            showLabels ? (
                <Typography key="hours" component="span" fontSize={18} fontWeight={600} color={color}>
                    {`${formatNumber(hours)}h`}
                </Typography>
            ) : (
                <Typography key="hours" component="span" fontSize={18} fontWeight={600} color={color}>
                    {`${formatNumber(hours)}:`}
                </Typography>
            )
        );
    }

    if (minutes !== undefined) {
        timeComponents.push(
            showLabels ? (
                <Typography key="minutes" component="span" fontSize={18} fontWeight={600} color={color}>
                    {`${formatNumber(minutes)}m`}
                </Typography>
            ) : (
                <Typography key="minutes" component="span" fontSize={18} fontWeight={600} color={color}>
                    {`${formatNumber(minutes)}:`}
                </Typography>
            )
        );
    }

    if (seconds !== undefined && seconds >= 0) {
        timeComponents.push(
            showLabels ? (
                <Typography key="seconds" component="span" fontSize={18} fontWeight={600} color={color}>
                    {`${formatNumber(seconds)}s`}
                </Typography>
            ) : (
                <Typography key="seconds" component="span" fontSize={18} fontWeight={600} color={color}>
                    {formatNumber(seconds)}
                </Typography>
            )
        );
    }

    return (
        <Stack direction="row" component="span" spacing={showLabels ? 0.5 : 0} alignItems="center">
            {timeComponents.map((component, index) => (
                <React.Fragment key={index}>
                    {component}
                    {showLabels && index < timeComponents.length - 1 && (
                        <Typography component="span" fontSize={18} fontWeight={600} color={color}>
                            {' '}
                        </Typography>
                    )}
                </React.Fragment>
            ))}
        </Stack>
    );
};

export default Counter;
